<template>
  <div class="import-page">
    <!-- 顶部 -->
    <el-card class="tool-card" shadow="never">
      <div class="tool-row">
        <div class="tool-left">
          <span class="tool-title">Excel 导入</span>
          <span class="tool-sub">先下载模板 → 按模板填入数据 → 上传导入，单行失败不影响其他行</span>
        </div>
      </div>
    </el-card>

    <!-- 类型选择 -->
    <el-card class="tool-card" shadow="never">
      <div class="type-tabs">
        <div
          v-for="t in types"
          :key="t.value"
          class="type-item"
          :class="{ active: curType === t.value }"
          @click="switchType(t.value)"
        >
          <i :class="t.icon" class="type-icon"></i>
          <div class="type-name">{{ t.label }}</div>
          <div class="type-desc">{{ t.desc }}</div>
        </div>
      </div>

      <!-- 模板说明 + 下载 -->
      <div class="tpl-block">
        <div class="tpl-head">
          <span class="tpl-title">{{ curTypeObj.label }}模板字段</span>
          <el-button size="small" icon="el-icon-download" @click="downloadTemplate">下载导入模板</el-button>
        </div>
        <div class="tpl-cols">
          <template v-for="(c, i) in curTypeObj.cols">
            <span :key="i" class="tpl-col" :class="{ required: c.required }">{{ c.name }}{{ c.required ? ' *' : '' }}</span>
          </template>
        </div>
        <div v-for="(tip, i) in curTypeObj.tips" :key="'tip' + i" class="tpl-tip">· {{ tip }}</div>
      </div>

      <!-- 上传 -->
      <div class="upload-block">
        <el-upload
          drag
          :action="uploadUrl"
          name="file"
          :accept="accept"
          :show-file-list="false"
          :on-success="onUploadSuccess"
          :on-error="onUploadError"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
          <div class="el-upload__tip" slot="tip">
            仅支持 xls / xlsx / csv，文件 ≤ 5MB，单次最多 1000 行。导入失败可下载失败明细。
          </div>
        </el-upload>
      </div>

      <!-- 结果 -->
      <div v-if="result" class="result-block" :class="{ failed: result.fail > 0 }">
        <div class="result-line">
          本次导入：总数 <b>{{ result.total }}</b>，成功 <b class="ok">{{ result.success }}</b>，失败 <b class="bad">{{ result.fail }}</b>
          <el-button
            v-if="failFileUrl"
            size="small"
            type="danger"
            plain
            icon="el-icon-download"
            class="fail-dl"
            @click="downloadFail"
          >下载失败明细</el-button>
        </div>
        <div v-if="result.errors && result.errors.length" class="err-list">
          <div v-for="(e, i) in result.errors" :key="i" class="err-item">
            <span class="err-row">第 {{ e.row }} 行</span>{{ e.reason }}
          </div>
        </div>
      </div>
    </el-card>

    <!-- 批次记录 -->
    <el-card class="tool-card" shadow="never">
      <div class="tool-row">
        <div class="tool-title">导入记录</div>
        <el-radio-group v-model="logType" size="small" @change="loadLogs">
          <el-radio-button label="0">全部</el-radio-button>
          <el-radio-button label="1">线索</el-radio-button>
          <el-radio-button label="2">订单</el-radio-button>
          <el-radio-button label="3">分期</el-radio-button>
          <el-radio-button label="4">账单</el-radio-button>
        </el-radio-group>
      </div>
      <el-table
        v-loading="logLoading"
        :data="logs"
        :header-cell-style="{ background: '#F7F9FC', color: '#18233C', fontWeight: 600 }"
        empty-text="暂无导入记录"
      >
        <el-table-column prop="filename" label="文件名" min-width="220" />
        <el-table-column prop="type_text" label="类型" width="80" align="center" />
        <el-table-column label="结果" width="180">
          <template slot-scope="{ row }">
            成功 <span class="t-ok">{{ row.success }}</span> / 失败 <span class="t-fail">{{ row.fail }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="operator_text" label="导入人" width="100" />
        <el-table-column label="时间" min-width="150">
          <template slot-scope="{ row }">{{ fmtDateTime(row.createtime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="140" align="center">
          <template slot-scope="{ row }">
            <el-button v-if="row.has_fail_file" type="text" size="small" class="del-btn" @click="downloadFail(row)">下载失败明细</el-button>
            <span v-else>—</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import { importLogList } from '@/api/import'
import { API_BASE } from '@/config'

const TYPES = {
  1: {
    label: '线索导入', icon: 'el-icon-user', desc: '推广小组录入线索',
    cols: [{ name: '来源渠道', required: true }, { name: '量账号', required: true }, { name: '量名称' }, { name: '目的' }],
    tips: ['来源渠道须为本机构已启用的渠道','目的填：未知 / 学习 / 兼职，可留空']
  },
  2: {
    label: '订单导入', icon: 'el-icon-tickets', desc: '销售/管理员录入订单',
    cols: [
      { name: '商品名称', required: true }, { name: '售价', required: true }, { name: '客户姓名', required: true },
      { name: '客户手机号', required: true }, { name: '量账号', required: true }, { name: '省' }, { name: '市' },
      { name: '区' }, { name: '销售账号' }, { name: '备注' }
    ],
    tips: ['售价须在商品可售区间内','机构管理员导入时销售账号必填，销售导入可不填','量账号即线索的量账号']
  },
  3: {
    label: '分期导入', icon: 'el-icon-coin', desc: '为已成交订单创建分期',
    cols: [{ name: '订单号', required: true }, { name: '收款平台', required: true }, { name: '分期总额', required: true }, { name: '期数', required: true }, { name: '首次还款日', required: true }],
    tips: ['订单号须为本机构已存在订单','期数 2-12，首次还款日不能早于今天','收款平台须为本机构已启用平台']
  },
  4: {
    label: '账单导入', icon: 'el-icon-money', desc: '登记收款账单（待审核）',
    cols: [{ name: '订单号', required: true }, { name: '收款平台', required: true }, { name: '金额', required: true }, { name: '第三方单号' }, { name: '是否计入业绩' }, { name: '凭证' }],
    tips: ['是否计入业绩填：是 / 否 / 留空(默认是)','凭证为可访问的图片链接，选填']
  }
}

export default {
  name: 'ImportExcel',
  data() {
    return {
      curType: 1,
      logType: '0',
      logLoading: false,
      logs: [],
      result: null,
      lastFailLogId: 0
    }
  },
  computed: {
    types() {
      return [1, 2, 3, 4].map(v => ({ value: v, ...TYPES[v] }))
    },
    curTypeObj() {
      return TYPES[this.curType]
    },
    token() {
      return this.$store.state.token || localStorage.getItem('crm_token') || ''
    },
    uploadUrl() {
      return `${API_BASE}/import/upload?type=${this.curType}&token=${encodeURIComponent(this.token)}`
    },
    accept() {
      return '.xls,.xlsx,.csv'
    },
    failFileUrl() {
      return this.lastFailLogId ? this.failDlUrl(this.lastFailLogId) : ''
    }
  },
  created() {
    this.loadLogs()
  },
  methods: {
    switchType(v) {
      this.curType = v
      this.result = null
      this.lastFailLogId = 0
    },
    downloadTemplate() {
      window.open(`${API_BASE}/import/template?type=${this.curType}&token=${encodeURIComponent(this.token)}`, '_blank')
    },
    failDlUrl(logId) {
      return `${API_BASE}/import/failDownload?log_id=${logId}&token=${encodeURIComponent(this.token)}`
    },
    onUploadSuccess(res) {
      if (res && res.code === 1) {
        const d = res.data || {}
        this.result = d
        this.lastFailLogId = d.fail && d.log_id ? d.log_id : 0
        this.loadLogs()
      }
    },
    onUploadError() {
      this.$message.error('上传失败，请检查网络或文件')
    },
    downloadFail(row) {
      const logId = row && row.id ? row.id : this.lastFailLogId
      if (!logId) return
      window.open(this.failDlUrl(logId), '_blank')
    },
    loadLogs() {
      this.logLoading = true
      importLogList({ type: Number(this.logType) })
        .then(data => { this.logs = data.list || [] })
        .catch(() => { this.logs = [] })
        .finally(() => { this.logLoading = false })
    },
    fmtDateTime(ts) {
      if (!ts) return '—'
      const d = new Date(parseInt(ts) * 1000)
      const p = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.import-page { padding: 0; }
.tool-card { margin-bottom: 14px; border-radius: 10px; border: 1px solid #E5E9F2; }
.tool-row { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.tool-left { display: flex; align-items: baseline; gap: 12px; }
.tool-title { font-size: 15px; font-weight: 700; color: #18233C; }
.tool-sub { font-size: 12px; color: #8A94AD; }

.type-tabs { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 18px; }
.type-item {
  border: 1px solid #E5E9F2; border-radius: 10px; padding: 16px;
  cursor: pointer; text-align: center; transition: all .2s; background: #fff;
}
.type-item:hover { box-shadow: 0 6px 18px rgba(54,104,252,.08); }
.type-item.active { border-color: #3668FC; background: #F5F8FF; }
.type-icon { font-size: 20px; color: #3668FC; }
.type-name { font-size: 14px; font-weight: 700; color: #18233C; margin-top: 6px; }
.type-desc { font-size: 12px; color: #8A94AD; margin-top: 4px; }

.tpl-block { background: #F7F9FC; border-radius: 10px; padding: 14px 16px; margin-bottom: 18px; }
.tpl-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.tpl-title { font-size: 13px; font-weight: 700; color: #18233C; }
.tpl-cols { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.tpl-col {
  background: #fff; border: 1px solid #E5E9F2; border-radius: 6px;
  padding: 4px 10px; font-size: 12px; color: #5A6580;
}
.tpl-col.required { color: #E5484D; border-color: #F3C9CB; background: #FEF5F5; }
.tpl-tip { font-size: 12px; color: #8A94AD; line-height: 1.8; }

.upload-block { }
.result-block { margin-top: 16px; background: #F2F9F4; border: 1px solid #CDEBDA; border-radius: 10px; padding: 14px 16px; }
.result-block.failed { background: #FEF5F5; border-color: #F3C9CB; }
.result-line { font-size: 14px; color: #18233C; }
.result-line .ok { color: #34A46F; }
.result-line .bad { color: #E5484D; }
.fail-dl { margin-left: 12px; }
.err-list { margin-top: 10px; max-height: 160px; overflow-y: auto; }
.err-item { font-size: 12px; color: #E5484D; line-height: 1.9; }
.err-row { font-weight: 700; margin-right: 6px; }

.t-ok { color: #34A46F; font-weight: 700; }
.t-fail { color: #E5484D; font-weight: 700; }
.del-btn { color: #E5484D; }
::v-deep .el-button--primary { background: #3668FC; border-color: #3668FC; }
::v-deep .el-button--primary:hover,
::v-deep .el-button--primary:focus { background: #5B8DFF; border-color: #5B8DFF; }
::v-deep .el-upload-dragger { width: 100%; }
::v-deep .el-table__row:hover > td { background: #F5F8FF !important; }
::v-deep .el-dialog { border-radius: 12px; }
</style>